// frontend/src/components/PostStatsDrawer.js
import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, IconButton, Chip, Divider,
  CircularProgress, Alert, alpha, useTheme, Button, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CommentIcon from '@mui/icons-material/Comment';
import ShareIcon from '@mui/icons-material/Share';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import ForumIcon from '@mui/icons-material/Forum';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import StarIcon from '@mui/icons-material/Star';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { importedPostsApi, analyticsApi } from '../services/api';

const PLATFORM_META = {
  youtube:   { Icon: YouTubeIcon,   color: '#FF0000', label: 'YouTube' },
  instagram: { Icon: InstagramIcon, color: '#E1306C', label: 'Instagram' },
  facebook:  { Icon: FacebookIcon,  color: '#1877F2', label: 'Facebook' },
};

function KpiBox({ icon: Icon, label, value, color }) {
  return (
    <Box sx={{ flex: 1, textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha(color, 0.06), border: `1px solid ${alpha(color, 0.15)}` }}>
      <Icon sx={{ fontSize: 20, color, mb: 0.5 }} />
      <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
        {Number(value) >= 1000 ? `${(value / 1000).toFixed(1)}k` : Number(value).toLocaleString()}
      </Typography>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
    </Box>
  );
}

const PostStatsDrawer = ({ postId, open, onClose }) => {
  const theme = useTheme();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [impact, setImpact] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');
  const [rescanning, setRescanning] = useState(false);

  useEffect(() => {
    if (!open || !postId) return;
    setLoading(true);
    setError('');
    setPost(null);
    setImpact(null);
    setRefreshMsg('');
    Promise.all([
      importedPostsApi.getPost(postId),
      analyticsApi.getPostImpact(postId).catch(() => null),
    ])
      .then(([postData, impactData]) => {
        setPost(postData);
        setImpact(impactData);
      })
      .catch(err => setError(err.message || 'Failed to load post details'))
      .finally(() => setLoading(false));
  }, [open, postId]);

  const handleRefreshStats = async () => {
    if (!postId || refreshing) return;
    setRefreshing(true);
    setRefreshMsg('');
    setError('');
    try {
      const result = await importedPostsApi.refreshStats(postId);
      // Merge fresh stats into the displayed post
      setPost(prev => prev ? { ...prev, stats: result.stats, synced_at: result.synced_at } : prev);
      setRefreshMsg('Stats updated!');
      setTimeout(() => setRefreshMsg(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to refresh stats');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRescanComments = async () => {
    if (!postId || rescanning) return;
    setRescanning(true);
    setRefreshMsg('');
    setError('');
    try {
      const result = await importedPostsApi.rescanComments(postId);
      const { fetched = 0, replied = 0, queued = 0, already_replied = 0 } = result;
      const parts = [];
      if (fetched > 0)         parts.push(`${fetched} new comment${fetched !== 1 ? 's' : ''} fetched`);
      if (replied > 0)         parts.push(`${replied} repl${replied !== 1 ? 'ies' : 'y'} sent`);
      if (queued > 0)          parts.push(`${queued} queued for review`);
      if (already_replied > 0) parts.push(`${already_replied} already replied`);
      setRefreshMsg(parts.length > 0
        ? parts.join(' · ')
        : 'No new comments found.');
      setTimeout(() => setRefreshMsg(''), 6000);
    } catch (err) {
      setError(err.message || 'Failed to refresh comments');
    } finally {
      setRescanning(false);
    }
  };

  const meta = post ? (PLATFORM_META[post.platform] || { color: '#6366f1', label: post.platform }) : {};
  const stats = post?.stats || {};
  const history = post?.growth_history || [];

  // Format chart data — label by date
  const chartData = history.map(h => ({
    date: new Date(h.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    Views:    h.views,
    Likes:    h.likes,
    Comments: h.comments,
  }));

  const engagementRate = stats.views > 0
    ? ((stats.likes + stats.comments) / stats.views * 100).toFixed(2)
    : '0.00';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 0 } }}
    >
      {/* Header */}
      <Box sx={{
        p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: meta.color ? alpha(meta.color, 0.04) : 'background.paper',
      }}>
        {meta.Icon && <meta.Icon sx={{ color: meta.color, fontSize: 22 }} />}
        <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>Post Analytics</Typography>
        {post && (
          <>
            <Tooltip title="Fetch latest likes, comments & views from the platform">
              <Button
                size="small"
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                onClick={handleRefreshStats}
                disabled={refreshing}
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', mr: 0.5 }}
              >
                {refreshing ? 'Syncing…' : 'Refresh Stats'}
              </Button>
            </Tooltip>
            <Tooltip title="Refresh comments — fetches new comments and generates AI replies for unreplied ones">
              <Button
                size="small"
                variant="outlined"
                startIcon={rescanning ? <CircularProgress size={14} color="inherit" /> : <ForumIcon />}
                onClick={handleRescanComments}
                disabled={rescanning}
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', mr: 0.5 }}
              >
                {rescanning ? 'Refreshing…' : 'Refresh Comments'}
              </Button>
            </Tooltip>
          </>
        )}
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 3, overflowY: 'auto', height: '100%' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ borderRadius: 3, mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {refreshMsg && <Alert severity="success" sx={{ borderRadius: 3, mb: 2 }}>{refreshMsg}</Alert>}

        {post && !loading && (
          <>
            {/* Thumbnail + title */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              {post.media_url && (
                <Box
                  component="img"
                  src={post.media_url}
                  alt=""
                  sx={{ width: 100, height: 68, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  {meta.Icon && <meta.Icon sx={{ fontSize: 14, color: meta.color }} />}
                  <Typography variant="caption" sx={{ color: meta.color, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    {meta.label}
                  </Typography>
                  {post.media_type && (
                    <Chip label={post.media_type} size="small" sx={{ height: 16, fontSize: '0.65rem', fontWeight: 700 }} />
                  )}
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.4 }}>
                  {post.title || '(no title)'}
                </Typography>
                {post.published_at && (
                  <Typography variant="caption" color="text.disabled">
                    Published {new Date(post.published_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            {/* KPI grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 3 }}>
              <KpiBox icon={VisibilityIcon} label="Views"       value={stats.views    || 0} color="#6366f1" />
              <KpiBox icon={ThumbUpIcon}   label="Likes"       value={stats.likes    || 0} color="#0284c7" />
              <KpiBox icon={CommentIcon}   label="Comments"    value={stats.comments || 0} color="#0d9488" />
              <KpiBox icon={TrendingUpIcon} label="Engagement %" value={engagementRate} color="#f97316" />
            </Box>

            {stats.shares > 0 && (
              <Box sx={{ mb: 3 }}>
                <KpiBox icon={ShareIcon} label="Shares" value={stats.shares} color="#8b5cf6" />
              </Box>
            )}

            <Divider sx={{ mb: 2.5 }} />

            {/* Growth chart */}
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
              Growth Over Syncs
            </Typography>

            {history.length < 2 ? (
              <Box sx={{
                p: 3, textAlign: 'center', borderRadius: 3,
                border: '2px dashed', borderColor: 'divider',
              }}>
                <TrendingUpIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  {history.length === 0 ? 'No snapshots yet. Sync this platform to start tracking.' : 'Sync again later to see growth trends.'}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                  Each sync adds a data point to this chart.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <defs>
                      <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={meta.color || '#6366f1'} stopOpacity={0.2} />
                        <stop offset="95%" stopColor={meta.color || '#6366f1'} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gLikes" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#0284c7" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#0284c7" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <RechartsTooltip
                      contentStyle={{
                        background: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 10,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="Views"    stroke={meta.color || '#6366f1'} strokeWidth={2.5} fill="url(#gViews)" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    <Area type="monotone" dataKey="Likes"    stroke="#0284c7"                 strokeWidth={2}   fill="url(#gLikes)" dot={{ r: 3 }} />
                    <Area type="monotone" dataKey="Comments" stroke="#0d9488"                 strokeWidth={2}   fill="none"         dot={{ r: 3 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}

            {/* ── Follower Impact (ROI) section ── */}
            <Divider sx={{ my: 2.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <PeopleAltIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
              <Typography variant="subtitle2" fontWeight={800}>Follower Impact</Typography>
              {impact?.roi_score != null && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: 13, color: '#f59e0b !important' }} />}
                  label={`ROI ${Number(impact.roi_score).toFixed(1)} / 10`}
                  size="small"
                  sx={{ ml: 'auto', fontWeight: 800, bgcolor: alpha('#f59e0b', 0.12), color: '#b45309', border: `1px solid ${alpha('#f59e0b', 0.3)}` }}
                />
              )}
            </Box>

            {!impact || (impact.follower_delta_24h == null && impact.follower_delta_48h == null && impact.follower_delta_72h == null) ? (
              <Box sx={{ p: 2.5, textAlign: 'center', borderRadius: 3, border: '2px dashed', borderColor: 'divider' }}>
                <PeopleAltIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.75 }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Follower impact data not yet available
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                  Tracked 24h, 48h, and 72h after publish. Check back later.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Delta bars */}
                {(() => {
                  const deltas = [
                    { label: '24h', value: impact.follower_delta_24h },
                    { label: '48h', value: impact.follower_delta_48h },
                    { label: '72h', value: impact.follower_delta_72h },
                  ].filter(d => d.value != null);
                  const absMax = Math.max(...deltas.map(d => Math.abs(d.value)), 1);
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
                      {deltas.map(({ label, value }) => {
                        const pct = Math.abs(value) / absMax * 100;
                        const positive = value >= 0;
                        const barColor = positive ? '#10b981' : '#ef4444';
                        return (
                          <Box key={label}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                              <Typography variant="caption" fontWeight={700} color="text.secondary">{label} after post</Typography>
                              <Typography variant="caption" fontWeight={800} sx={{ color: barColor }}>
                                {positive ? '+' : ''}{value.toLocaleString()} followers
                              </Typography>
                            </Box>
                            <Box sx={{ height: 8, bgcolor: alpha(barColor, 0.12), borderRadius: 99, overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: barColor, borderRadius: 99, transition: 'width 0.6s ease' }} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}

                {/* Churn warning */}
                {impact.follower_delta_72h != null && impact.follower_delta_72h < -50 && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, borderRadius: 2, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.2)}` }}>
                    <WarningAmberIcon sx={{ fontSize: 16, color: '#ef4444', mt: 0.1, flexShrink: 0 }} />
                    <Typography variant="caption" fontWeight={600} color="error.main">
                      This post was associated with a significant follower drop ({impact.follower_delta_72h.toLocaleString()} in 72h). Review content for potential audience churn.
                    </Typography>
                  </Box>
                )}

                {/* Baseline note */}
                {impact.follower_baseline != null && (
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                    Baseline at publish: {Number(impact.follower_baseline).toLocaleString()} followers
                  </Typography>
                )}
              </>
            )}

            <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              Last synced: {post.synced_at ? new Date(post.synced_at).toLocaleString() : 'unknown'}
            </Typography>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default PostStatsDrawer;
